import type { MetaDescriptor } from 'react-router';
import type { Storefront } from '~/domain/core/index.server';
import { toConfiguredLocalePrefix } from '~/lib/localization/locale';
import { buildSeo } from './build-seo';
import { formatTitle } from './format-title';
import { shouldIndex } from './should-index';

export { buildSeo } from './build-seo';
export { formatTitle } from './format-title';
export { shouldIndex } from './should-index';

/**
 * The slice of storefront context that the SEO feature needs. Derived from
 * the canonical `Storefront` shape so a rename in `domain/core` surfaces
 * as a type error here. Routes pass this in from the root loader's data.
 */
export type StorefrontSeoContext = Pick<
  Storefront,
  'fqdn' | 'languages' | 'defaultLanguage' | 'resolvedLocale'
> & {
  companyName: Storefront['companyProfile']['name'] | null | undefined;
  preview: Storefront['companyProfile']['preview'];
  seoEnabled: Storefront['preferences']['seo'];
};

type PageSeoInput = {
  storefront: StorefrontSeoContext;
  /**
   * Builds the path for a given locale. Pass through to react-router's
   * `href`; the locale argument forwards directly to the optional
   * `:locale?` segment, so passing `undefined` produces a default-language
   * path with no prefix:
   *
   *   buildPath: (locale) => href('/:locale?/experience/:experienceId', { locale, experienceId })
   */
  buildPath: (locale: string | undefined) => string;
  /**
   * The page-specific title fragment — gets concatenated with the company
   * name as `${pageTitle} | ${companyName}`. For an experience page this is
   * the experience name; for a generic page it might be a step label.
   *
   * Optional: omit it when the page renders its own `<title>` (e.g. the terms
   * pages set a localized title in the component), and `buildPageSeo` will emit
   * canonical/hreflang/OG tags without a competing `<title>`. Pass `''` to make
   * the title just the company name (e.g. the home page).
   */
  pageTitle?: string;
  /** Plain-text or markdown description. Markdown is stripped automatically. */
  description?: string | null;
  /** Open Graph image URL. */
  ogImage?: string | null;
  /** Open Graph type. Defaults to `'website'`. */
  ogType?: string;
  /** Optional query parameters to include in the canonical URL. */
  query?: Record<string, string | undefined>;
};

/**
 * Build the full set of SEO meta tags for a page.
 *
 * Orchestrates the lower-level helpers — resolves noindex / nofollow from
 * the storefront, formats the title, and feeds everything through
 * `buildSeo`. Routes shouldn't need to call any of these helpers directly.
 */
export function buildPageSeo(input: PageSeoInput): MetaDescriptor[] {
  const indexable = shouldIndex({
    preview: input.storefront.preview,
    seoEnabled: input.storefront.seoEnabled,
  });

  // Canonical uses the configured form of the *resolved* locale (default →
  // no prefix), so locale aliases (`/en`, `/en-us`) collapse to one canonical.
  const canonicalLocale = toConfiguredLocalePrefix(
    input.storefront.resolvedLocale,
    input.storefront.languages,
    input.storefront.defaultLanguage
  );

  return buildSeo({
    host: input.storefront.fqdn,
    languages: input.storefront.languages,
    defaultLanguage: input.storefront.defaultLanguage,
    canonicalLocale,
    buildPath: input.buildPath,
    query: input.query,
    // Only emit a <title> when the caller supplies pageTitle; pages that render
    // their own <title> omit it to avoid a duplicate tag.
    title:
      input.pageTitle === undefined
        ? null
        : formatTitle(input.pageTitle, input.storefront.companyName),
    description: input.description,
    ogImage: input.ogImage,
    ogType: input.ogType,
    noindex: !indexable,
    nofollow: !indexable,
  });
}
